import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Radio, Clock, Share2, Heart, ChevronLeft, ChevronRight } from 'lucide-react';
import { Logo } from '@/components/Logo';
import { SutonMicIcon } from '@/components/SutonMicIcon';
import { useAudioStore } from '@/hooks/useAudioStore';
import { useAudioPlayer } from '@/hooks/useAudioPlayer';
import { PlaybackControls } from '@/components/PlaybackControls';
import { ShareToast } from '@/components/ShareToast';
import { TimerModal } from '@/components/TimerModal';
import { WaveformVisualizer } from '@/components/WaveformVisualizer';
import { MarqueeText } from '@/components/MarqueeText';
import { shareStation, type ShareResult } from '@/utils/share';
import { fetchStreams } from '@/services/strapi';
import { getInitialStations } from '@/constants/stations';
import { getStreamColor, getSolidColor } from '@/utils/streamColor';
import { useLiveStatus } from '@/hooks/useLiveStatus';
import { useLiveVideoStatus } from '@/hooks/useLiveVideoStatus';
import { maybeTransliterate } from '@/utils/transliterate';
import StationCard from '@/components/StationCard';
import type { RadioStation } from '@/types';

const SCROLL_AMOUNT = 200; // px za jedan klik strelice

export const HomeScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language || 'de';
  const scrollRef = useRef<HTMLDivElement>(null);
  const [streams, setStreams] = useState<RadioStation[]>(() => getInitialStations());
  const [showTimerModal, setShowTimerModal] = useState(false);
  const [shareFeedback, setShareFeedback] = useState<ShareResult | null>(null);
  const { currentStation, currentTrack, isPlaying, favorites, addFavorite, removeFavorite, sleepTimer, setSleepTimer, clearSleepTimer } = useAudioStore();
  const { playStation, playPrevStation, playNextStation } = useAudioPlayer();

  // A5: LIVE VIDEO header — pollovan iz MediaMTX status endpoint-a.
  // Hook polluje svakih 30s na video.radio-suton.de/api/status; zadržava last-known
  // stanje na network error-u (ne flickeruje siv ↔ crven).
  const { status: liveVideoStatus } = useLiveVideoStatus({ enabled: true });
  const isLiveVideoActive = liveVideoStatus.live;

  const handleStationSelect = (station: RadioStation) => {
    playStation(station);
    navigate('/app/player');
  };

  useEffect(() => {
    fetchStreams(locale).then((s) => {
      if (s.length > 0) {
        setStreams(s);
        const store = useAudioStore.getState();
        const current = store.currentStation;
        if (!current) {
          store.setCurrentStation(s[0]);
        } else {
          const match = s.find(
            (x) => x.streamUrl === current.streamUrl || x.id === current.id
          );
          if (match) {
            const hasChanges =
              match.artwork !== current.artwork ||
              match.name !== current.name ||
              match.description !== current.description ||
              match.color !== current.color ||
              match.genre !== current.genre;
            if (hasChanges) {
              store.setCurrentStation({ ...current, ...match });
            }
          } else {
            // Legacy testni URL ili orphan — reset na prvu iz svježe liste
            const url = current.streamUrl || '';
            if (/zeno\.fm|streamupsolutions|balkanmusic|radios3|banovina|dzungla|morava|bigfm|pink\.rs/i.test(url)) {
              store.setCurrentStation(s[0]);
            }
          }
        }
      }
    });
  }, [locale]);

  const isFavorite = currentStation ? favorites.includes(currentStation.id) : false;

  const liveStatus = useLiveStatus();
  const isLiveStation =
    Boolean(currentStation?.streamUrl?.includes('suton_radio')) ||
    Boolean(currentStation?.name?.toLowerCase().includes('live'));
  const showLiveInfo = isLiveStation && Boolean(liveStatus?.isLive);
  const showAutoDjBanner = isLiveStation && liveStatus != null && !liveStatus.isLive;

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const scrollLeft = () => {
    scrollRef.current?.scrollBy({ left: -SCROLL_AMOUNT, behavior: 'smooth' });
  };
  const scrollRight = () => {
    scrollRef.current?.scrollBy({ left: SCROLL_AMOUNT, behavior: 'smooth' });
  };

  const handleFavoriteToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (currentStation) {
      if (isFavorite) removeFavorite(currentStation.id);
      else addFavorite(currentStation.id);
    }
  };

  const handleTimerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowTimerModal(true);
  };
  const handleShareClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const result = await shareStation(
      currentStation?.name ?? 'Suton Radio',
      currentStation?.description
    );
    setShareFeedback(result);
  };

  return (
    <div className="min-h-screen relative">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{ 
          backgroundImage: 'url(/bg-home.png)',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary-bg/50 to-primary-bg" />
      </div>

      {/* Content */}
      <div className="relative z-10 px-4 pt-6 pb-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-8"
        >
          <Logo
            color={currentStation ? getSolidColor(getStreamColor(currentStation.color, currentStation.name)) : '#3b82f6'}
          />
          <motion.button
            type="button"
            disabled={!isLiveVideoActive}
            onClick={() => {
              if (!isLiveVideoActive) return;
              navigate('/app/live-video');
            }}
            aria-label={t('liveVideo.title')}
            aria-disabled={!isLiveVideoActive}
            className={`w-12 h-12 rounded-full glass flex items-center justify-center transition-colors ${
              isLiveVideoActive
                ? 'hover:bg-red-500/20 cursor-pointer'
                : 'cursor-not-allowed opacity-70'
            }`}
            animate={
              isLiveVideoActive
                ? { scale: [1, 1.15, 1], opacity: [1, 0.6, 1] }
                : { scale: 1, opacity: 0.7 }
            }
            transition={
              isLiveVideoActive
                ? { duration: 0.8, repeat: Infinity, ease: 'easeInOut' }
                : { duration: 0 }
            }
            style={{ color: isLiveVideoActive ? '#EF4444' : '#6B7280' }}
          >
            <SutonMicIcon size={22} className="block" />
          </motion.button>
        </motion.div>

        {/* Main Player Card - klik otvara punozaslonski player + horizontalni swipe mijenja stanicu */}
        {/* R7.1: drag flag — ako je gesture bio drag (>10px), ne tretiraj klik kao otvori-player */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          drag="x"
          dragDirectionLock
          dragSnapToOrigin
          dragMomentum={false}
          onDragEnd={(_, info) => {
            const SWIPE_OFFSET_THRESHOLD = 50;
            const SWIPE_VELOCITY_THRESHOLD = 300;
            const swipeLeft = info.offset.x <= -SWIPE_OFFSET_THRESHOLD || info.velocity.x <= -SWIPE_VELOCITY_THRESHOLD;
            const swipeRight = info.offset.x >= SWIPE_OFFSET_THRESHOLD || info.velocity.x >= SWIPE_VELOCITY_THRESHOLD;
            if (swipeLeft) {
              playNextStation(streams);
            } else if (swipeRight) {
              playPrevStation(streams);
            }
          }}
          onClick={(e) => {
            const target = e.target as HTMLElement;
            // Ako klik dolazi iz nested button (Quick Actions ili sl.), onClick handler tog dugmeta
            // već ima stopPropagation. Sigurnosna mreža za drag-vs-tap: ako je drag pomakao Card,
            // framer ne emituje 'click' (sintetički event); ali ako se klikne mirno, otvori player.
            if (target.closest('button')) return;
            if (currentStation) navigate('/app/player');
          }}
          style={{ touchAction: 'pan-y pinch-zoom' }}
          className={`glass rounded-3xl p-6 mb-8 select-none cursor-grab active:cursor-grabbing ${currentStation ? 'hover:bg-white/5 transition-colors' : ''}`}
        >
          {/* Artwork */}
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div
                className={`rounded-full ${showLiveInfo ? 'p-[3px] bg-red-500 shadow-[0_0_20px_rgba(239,68,68,0.45)] animate-pulse' : ''}`}
              >
                <motion.div
                  animate={isPlaying ? { rotate: 360 } : { rotate: 0 }}
                  transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                  className="flex-shrink-0 w-40 h-40 sm:w-48 sm:h-48 aspect-square rounded-full overflow-hidden border-4 transition-colors duration-300"
                  style={{ borderColor: currentStation ? `${getSolidColor(getStreamColor(currentStation.color, currentStation.name))}80` : 'rgba(59, 130, 246, 0.3)' }}
                >
                  {currentStation && currentStation.artwork && currentStation.artwork.startsWith('http') ? (
                    <img
                      src={currentStation.artwork}
                      alt={currentStation.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div
                      className="w-full h-full flex items-center justify-center"
                      style={{ background: currentStation ? getStreamColor(currentStation.color, currentStation.name) : 'var(--secondary-bg)' }}
                    >
                      <Radio className="w-16 h-16 text-white drop-shadow-lg" />
                    </div>
                  )}
                </motion.div>
              </div>

              {/* Playing Indicator */}
              {isPlaying && (
                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 glass px-4 py-2 rounded-full">
                  <WaveformVisualizer />
                </div>
              )}
            </div>
          </div>

          {/* Track Info */}
          <div className="text-center mb-6">
            <p className="text-sm font-semibold uppercase tracking-wider mb-2 transition-colors duration-300 flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
              {showLiveInfo && (
                <span className="inline-flex items-center gap-1.5 flex-shrink-0">
                  <span className="relative inline-flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75 animate-ping" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
                  </span>
                  <span className="text-red-500 font-bold normal-case">{t('player.live')}</span>
                  <span className="text-text-muted font-normal">•</span>
                </span>
              )}
              {showLiveInfo ? (
                <span className="bg-gradient-to-r from-fuchsia-500 via-violet-500 to-indigo-500 bg-clip-text text-transparent">
                  {currentStation?.name || t('home.selectStation')}
                </span>
              ) : (
                <span style={{ color: currentStation ? getSolidColor(getStreamColor(currentStation.color, currentStation.name)) : '#06b6d4' }}>
                  {currentStation?.name || t('home.selectStation')}
                </span>
              )}
            </p>
            {/* R7.1: MarqueeText umjesto wrap-ujućeg <h2> da krug logoa iznad ostane stabilan */}
            <div className="mb-1 px-1 h-8 flex items-center justify-center">
              <MarqueeText
                className="text-2xl font-bold leading-tight uppercase"
                text={
                  showLiveInfo
                    ? liveStatus?.nowPlaying
                      ? liveStatus.nowPlaying.artist
                        ? `${maybeTransliterate(liveStatus.nowPlaying.artist, locale)} - ${maybeTransliterate(liveStatus.nowPlaying.title, locale)}`
                        : maybeTransliterate(liveStatus.nowPlaying.text || liveStatus.nowPlaying.title, locale) ?? ''
                      : currentTrack
                        ? `${maybeTransliterate(currentTrack.artist, locale)}${currentTrack.artist ? ' - ' : ''}${maybeTransliterate(currentTrack.title, locale)}`
                        : maybeTransliterate(currentStation?.description, locale) || t('home.welcomeSuton')
                    : currentTrack
                      ? `${maybeTransliterate(currentTrack.artist, locale)}${currentTrack.artist ? ' - ' : ''}${maybeTransliterate(currentTrack.title, locale)}`
                      : maybeTransliterate(currentStation?.description, locale) || t('home.welcomeSuton')
                }
              />
            </div>
            <p className="text-text-secondary flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm">
              {showLiveInfo ? (
                /* R7.1: brojač slušalaca uklonjen — ostaje samo streamer ime */
                <span className="font-medium">
                  {liveStatus?.streamerName?.trim()
                    ? maybeTransliterate(liveStatus.streamerName.trim(), locale)
                    : t('player.liveDj')}
                </span>
              ) : showAutoDjBanner ? (
                <span className="font-medium text-text-secondary">
                  {t('player.autoDj')} <span className="opacity-50 mx-1">•</span> {t('player.stationName')}
                </span>
              ) : (
                t('home.soundOfBalkans')
              )}
            </p>
          </div>

          {/* Playback Controls */}
          <PlaybackControls stationColor={currentStation ? getStreamColor(currentStation.color, currentStation.name) : undefined} />

          {/* Quick Actions - stopPropagation da klik ne otvara player */}
          <div className="flex justify-center gap-4 mt-6" onClick={(e) => e.stopPropagation()}>
            <button 
              onClick={handleFavoriteToggle}
              className={`flex items-center gap-2 px-4 py-2 rounded-full glass hover:bg-white/10 transition-colors ${
                isFavorite ? 'text-accent-pink' : ''
              }`}
            >
              <Heart className={`w-4 h-4 ${isFavorite ? 'fill-current' : ''}`} />
              <span className="text-sm">{t('home.favorites')}</span>
            </button>
            <button onClick={handleTimerClick} className="flex items-center gap-2 px-4 py-2 rounded-full glass hover:bg-white/10 transition-colors">
              <Clock className="w-4 h-4" />
              <span className="text-sm">
                {sleepTimer.isActive ? formatTime(sleepTimer.remainingSeconds) : t('home.timer')}
              </span>
            </button>
            <button onClick={handleShareClick} className="flex items-center gap-2 px-4 py-2 rounded-full glass hover:bg-white/10 transition-colors">
              <Share2 className="w-4 h-4" />
              <span className="text-sm">Podijeli</span>
            </button>
          </div>
        </motion.div>

        {/* Quick Play Stations */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h3 className="text-lg font-semibold mb-4">{t('home.quickAccess')}</h3>
          <div className="relative flex items-center gap-2">
            <button
              onClick={scrollLeft}
              aria-label={t('common.scrollLeft', 'Scroll left')}
              className="flex-shrink-0 w-10 h-10 rounded-full glass flex items-center justify-center hover:bg-white/10 transition-colors z-10"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div
              ref={scrollRef}
              className="flex gap-4 overflow-x-auto overflow-y-hidden flex-1 min-w-0 pb-4 scroll-smooth scroll-x-touch"
            >
            {streams.map((station, index) => {
              const isActive = currentStation?.id === station.id;
              // LIVE detekcija — naziv sadrži "live" ILI prva kartica (po order:asc)
              const isLive =
                station.name.toLowerCase().includes('live') || index === 0;
              return (
                <StationCard
                  key={station.id}
                  station={station}
                  isActive={isActive}
                  isLive={isLive}
                  onClick={() => handleStationSelect(station)}
                  index={index}
                />
              );
            })}
            </div>
            <button
              onClick={scrollRight}
              aria-label={t('common.scrollRight')}
              className="flex-shrink-0 w-10 h-10 rounded-full glass flex items-center justify-center hover:bg-white/10 transition-colors z-10"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </motion.div>
      </div>

      <TimerModal
        isOpen={showTimerModal}
        onClose={() => setShowTimerModal(false)}
        onSelect={setSleepTimer}
        onCancel={clearSleepTimer}
        isActive={sleepTimer.isActive}
        remainingSeconds={sleepTimer.remainingSeconds}
      />
      <ShareToast result={shareFeedback} onDismiss={() => setShareFeedback(null)} />
    </div>
  );
};
